import { useEffect, useRef } from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import {
  CalibrationHistoryEntry,
  CalibrationSettings,
  Skill,
  SettingsFormData,
} from 'talent_matching/types';

type InitialSkills = {
  top_skills: Skill[];
  scorecard_skills: Skill[];
  pipeline_skills: Skill[];
};

type Params = {
  isOpen: boolean;
  isHistoryTab: boolean;
  selectedHistory?: CalibrationHistoryEntry;
  settingsExist: boolean;
  settings?: CalibrationSettings | null;
  setValue: ReturnType<typeof useFormContext>['setValue'];
  reset: (values: Partial<SettingsFormData>) => void;
  buildFormValuesFromCalibration: (
    calibration: Partial<CalibrationSettings>
  ) => Partial<SettingsFormData>;
  hasJobDescription: boolean;
  hasScorecardAttributes: boolean;
  initialSkills?: InitialSkills;
  fetchedSuggestedPrompt?: string | null;
  setSuggestedPrompt: (value: string | null) => void;
  setSuggestedPromptError: (value: string | null) => void;
  applyingHistoryRef: React.MutableRefObject<boolean>;
};

export const useCalibrationFormStateSync = ({
  isOpen,
  isHistoryTab,
  selectedHistory,
  settingsExist,
  settings,
  setValue,
  reset,
  buildFormValuesFromCalibration,
  hasJobDescription,
  hasScorecardAttributes,
  initialSkills,
  fetchedSuggestedPrompt,
  setSuggestedPrompt,
  setSuggestedPromptError,
  applyingHistoryRef,
}: Params) => {
  const settingsInitializedRef = useRef(false);
  const lastHistoryIdRef = useRef<number | null>(null);
  const lastTabRef = useRef<boolean>(isHistoryTab);

  useEffect(() => {
    if (!isOpen) {
      settingsInitializedRef.current = false;
      lastHistoryIdRef.current = null;
      lastTabRef.current = isHistoryTab;
      applyingHistoryRef.current = false;
      return;
    }

    if (settingsExist && !settings) {
      return;
    }

    if (settingsExist && settings && settingsInitializedRef.current) {
      return;
    }

    // Defer initialization slightly to ensure form fields are fully registered
    setTimeout(() => {
      if (settingsExist && settings) {
        const formValues = buildFormValuesFromCalibration(settings);
        reset(formValues);

        setValue('extracted_skills', settings.extracted_skills, {
          shouldDirty: false,
        });
        if (settings.userPrompt) {
          setValue('userPrompt', settings.userPrompt, { shouldDirty: false });
          setValue('userPromptMode', 'view', { shouldDirty: false });
        }

        settingsInitializedRef.current = true;
      }

      setValue('include_job_description', hasJobDescription);
      setValue('include_scorecard_attributes', hasScorecardAttributes);

      if (!settings?.userPrompt) {
        setValue('userPromptMode', 'edit');
      }

      if (initialSkills) {
        setValue('jobPostSkills', initialSkills.top_skills);
        setValue('scorecardSkills', initialSkills.scorecard_skills);
        setValue(
          'pipelineSkills',
          initialSkills.pipeline_skills.map((skill: Skill) => ({
            ...skill,
            isPipelineSkill: true,
          }))
        );
      }
    }, 0);
  }, [
    isOpen,
    settingsExist,
    settings,
    buildFormValuesFromCalibration,
    reset,
    setValue,
    hasJobDescription,
    hasScorecardAttributes,
    initialSkills,
    isHistoryTab,
    applyingHistoryRef,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const tabChanged = lastTabRef.current !== isHistoryTab;
    lastTabRef.current = isHistoryTab;

    if (isHistoryTab && selectedHistory) {
      const historyId = selectedHistory.id;
      const historyChanged = lastHistoryIdRef.current !== historyId;

      if (tabChanged || historyChanged) {
        reset(buildFormValuesFromCalibration(selectedHistory));
        lastHistoryIdRef.current = historyId;
      }
    }

    if (!isHistoryTab && tabChanged && settingsExist && settings) {
      // Don't restore current settings if we just applied a history entry
      if (applyingHistoryRef.current) {
        applyingHistoryRef.current = false;
        return;
      }

      reset(buildFormValuesFromCalibration(settings));
      lastHistoryIdRef.current = null;
    }
  }, [
    isOpen,
    isHistoryTab,
    selectedHistory,
    settingsExist,
    settings,
    reset,
    buildFormValuesFromCalibration,
    applyingHistoryRef,
  ]);

  useEffect(() => {
    if (fetchedSuggestedPrompt) {
      setSuggestedPrompt(fetchedSuggestedPrompt);
      setSuggestedPromptError(null);
    }
  }, [fetchedSuggestedPrompt, setSuggestedPrompt, setSuggestedPromptError]);
};
